/**
 * domains/market/engines/setup2/engine.ts — MER Algo Footprints v7.1H
 *
 * Setup Engine v2 — public entry point.
 *
 * Pipeline: detect → score → readiness → penalties → disqualifiers
 *           → state → actionability → assemble → validate → sort
 *
 * All scoring is deterministic and explainable.
 * Audit includes input_hash, detection_trace, threshold_snapshot.
 */

import { nanoid }       from 'nanoid';
import {
  RichSetup, SetupEngineInput, SetupActionability,
  DEFAULT_SCORING_WEIGHTS, ScoringWeights,
  DEFAULT_THRESHOLDS, ThresholdSnapshot, DetectionTrace,
} from './types';
import {
  SETUP_ENGINE_VERSION, SCORING_VERSION, SCHEMA_VERSION,
  validateRichSetup,
} from './schema';
import { detectSetupCandidates, detectSetupCandidatesFull, SetupCandidate, SuppressedCandidate } from './detection';
import {
  scoreStructure, scoreContext, scoreLiquidity,
  scoreTrigger, scoreRiskReward, scoreExecutionQuality,
  computeQualityBreakdown, computeStopQuality,
  TriggerConfidence,
} from './scoring';
import {
  scoreReadiness, evaluatePenalties, evaluateDisqualifiers,
  deriveSetupState, ReadinessInput, SetupStateResult,
} from './readiness';

// ─── djb2 hash for input fingerprinting ──────────────────────────────────────
function hashInputs(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
  return (h >>> 0).toString(16).padStart(8, '0');
}

// ─── Classify trigger confidence from candidate ───────────────────────────────
function classifyTriggerConfidence(c: SetupCandidate): TriggerConfidence {
  if (!c.hasTrigger)         return 'none';
  if (!c.triggerSpecific)    return 'forming';
  if (!c.hasConfirmation)    return 'partial';
  return 'confirmed';
}

// ─── Classify spread assumption honestly from RVOL proxy ─────────────────────
function classifySpread(rvol: number): 'liquid' | 'normal' | 'unknown' {
  if (rvol >= 1.5) return 'liquid';
  if (rvol >= 0.8) return 'normal';
  return 'unknown';
}

// ─── Compute zone proximity (0–1) ────────────────────────────────────────────
function computeZoneProximity(
  zones: ReturnType<typeof detectSetupCandidates> extends any[] ? any : never,
  curPrice: number,
  nearKeyZone: boolean,
): number {
  if (!nearKeyZone) return 0;
  return 0.8; // present at key zone — graded further by atExecutionZone flag
}

// ─── Structure age helper ─────────────────────────────────────────────────────
function getStructureAge(smc: SetupEngineInput['smc'], candles_len: number): number {
  const bosAge   = smc?.lastBOS?.candleIdx   != null ? candles_len - 1 - smc.lastBOS.candleIdx   : Infinity;
  const chochAge = smc?.lastCHOCH?.candleIdx != null ? candles_len - 1 - smc.lastCHOCH.candleIdx : Infinity;
  return Math.min(bosAge, chochAge);
}

// ─── Setup age from real timestamps ───────────────────────────────────────────
// Uses actual Unix timestamps from StructureEvent/LiquiditySweep (both have .ts).
// Age is expressed in candle-interval units for consistency with structureAge.
// If interval cannot be determined (< 2 candles), falls back to candleIdx-based age.
function computeSetupAge(
  candidate:   import('./detection').SetupCandidate,
  smc:         SetupEngineInput['smc'],
  candles:     SetupEngineInput['candles'],
): number {
  const candles_len = candles.length;
  if (candles_len < 2) return 0;

  const lastCandleTs  = candles[candles_len - 1].ts;
  const prevCandleTs  = candles[candles_len - 2].ts;
  const intervalMs    = Math.max(1, lastCandleTs - prevCandleTs);

  // Primary event timestamp: choose the most relevant signal for this setup class
  let primaryEventTs: number | null = null;
  switch (candidate.setup_class) {
    case 'CONTINUATION':
    case 'BREAKOUT_CONFIRMATION':
      primaryEventTs = smc?.lastBOS?.ts ?? null;
      break;
    case 'REVERSAL':
    case 'TRAP_REVERSAL':
      // CHOCH is the trigger; fallback to sweep timestamp
      primaryEventTs = smc?.lastCHOCH?.ts ?? smc?.recentSweep?.ts ?? null;
      break;
    case 'LIQUIDITY_SWEEP':
      primaryEventTs = smc?.recentSweep?.ts ?? null;
      break;
    case 'MEAN_REVERSION':
      // No single structural event — use most recent structure event
      primaryEventTs = smc?.lastCHOCH?.ts ?? smc?.lastBOS?.ts ?? null;
      break;
    default:
      primaryEventTs = null;
  }

  if (primaryEventTs === null) return 0;

  // Convert time delta to candle-equivalent age
  const ageCandleUnits = Math.round((lastCandleTs - primaryEventTs) / intervalMs);
  return Math.max(0, ageCandleUnits);
}

// ─── Main assembly ────────────────────────────────────────────────────────────

function assembleRichSetup(
  candidate:  SetupCandidate,
  inp:        SetupEngineInput,
  weights:    ScoringWeights,
  thresholds: ThresholdSnapshot,
  traceEntry: DetectionTrace,
  inputHash:  string,
): RichSetup | null {
  const { smc, regime, traps, zones, trend, curPrice, candles } = inp;
  const candles_len = candles.length;
  const rvol        = regime?.rvol ?? 1.0;
  const lastCandleTs = candles[candles_len - 1]?.ts ?? Date.now();

  // Compute R/R
  const ep   = candidate.entryPriceIdeal ?? curPrice;
  const stop = candidate.stopLevel;
  const t1   = candidate.target1;
  let rr: number | null = null;
  if (stop !== null && t1 !== null && ep !== stop) {
    rr = parseFloat((Math.abs(t1 - ep) / Math.abs(ep - stop)).toFixed(2));
  }

  const triggerConfidence = classifyTriggerConfidence(candidate);
  const structureAge      = getStructureAge(smc, candles_len);
  const setupAge          = computeSetupAge(candidate, smc, candles);

  // ── Scoring ───────────────────────────────────────────────────────────────
  const structure_score = scoreStructure({ smc, direction: candidate.direction, setupClass: candidate.setup_class, candles_len });
  const context_score   = scoreContext({ regime: regime ?? null, trend: trend ?? null, direction: candidate.direction, setupClass: candidate.setup_class });
  const liquidity_score = scoreLiquidity({ smc, zones, curPrice, direction: candidate.direction, setupClass: candidate.setup_class });

  const trigger_score   = scoreTrigger({
    triggerConfidence,
    triggerSpecific:  candidate.triggerSpecific,
    hasConfirmation:  candidate.hasConfirmation,
    dispFreshness:    smc?.displacementFreshness ?? 0,
  });

  // Real stop quality from structural distance
  const stopQuality = computeStopQuality({
    stopLevel:      stop,
    entryPrice:     ep,
    smc,
    candles_len,
    stopLevelClear: candidate.stopLevelClear,
  });

  const rr_score = scoreRiskReward({
    rr,
    stopQuality,
    stopClear:   candidate.stopLevelClear,
    target1:     candidate.target1,
    target2:     candidate.target2,
    entryPrice:  ep,
    smc,
    zones,
  });

  const exec_score = scoreExecutionQuality({
    dispFreshness:    smc?.displacementFreshness ?? 0,
    dispStrength:     smc?.displacementStrength  ?? 0,
    rvol,
    atKeyZone:        candidate.atExecutionZone,
    spreadAssumption: classifySpread(rvol),
  });

  // ── Penalties ─────────────────────────────────────────────────────────────
  const penalties = evaluatePenalties({
    smc, regime: regime ?? null, trend: trend ?? null,
    traps, direction: candidate.direction,
    setupClass: candidate.setup_class, rvol,
    dispFreshness: smc?.displacementFreshness ?? 0,
    candles_len,
  });

  const quality = computeQualityBreakdown({
    structure: structure_score, context: context_score,
    liquidity: liquidity_score, trigger: trigger_score,
    risk_reward: rr_score, execution: exec_score,
    penalties, weights,
  });

  // ── Disqualifiers ─────────────────────────────────────────────────────────
  const disqualifiers = evaluateDisqualifiers({
    smc, regime: regime ?? null, traps,
    direction:      candidate.direction,
    setupClass:     candidate.setup_class,
    hasTrigger:     candidate.hasTrigger,
    stopLevelClear: candidate.stopLevelClear,
    rr,
    thresholds,
  });

  const fatalDisqs    = disqualifiers.filter(d => d.fatal);
  const hasFatalDisq  = fatalDisqs.length > 0;

  // ── Readiness ─────────────────────────────────────────────────────────────
  const zoneProximity = candidate.atExecutionZone
    ? (candidate.triggerFreshness >= 0.7 ? 0.9 : 0.7)
    : Math.max(0, candidate.triggerFreshness * 0.5);

  const readiness = scoreReadiness({
    hasTrigger:          candidate.hasTrigger,
    triggerFreshness:    candidate.triggerFreshness,
    triggerConfidence,
    hasConfirmation:     candidate.hasConfirmation,
    atExecutionZone:     candidate.atExecutionZone,
    zoneProximityPct:    zoneProximity,
    stopLevelClear:      candidate.stopLevelClear,
    rr,
    rvol,
    dispFreshness:       smc?.displacementFreshness ?? 0,
    lastCandleTs,
    thresholds,
  });

  // ── Lifecycle state ───────────────────────────────────────────────────────
  const stateResult = deriveSetupState({
    setupScore:        quality.setup_score,
    readinessScore:    readiness.readiness_score,
    hasFatalDisq,
    hasTrigger:        candidate.hasTrigger,
    triggerConfidence,
    hasConfirmation:   candidate.hasConfirmation,
    setupClass:        candidate.setup_class,
    structureAge:      structureAge === Infinity ? 999 : structureAge,
    setupAge,           // real age in candle-interval units from primary event timestamp
    thresholds,
  });
  const setup_state = stateResult.state;

  // ── Actionability — explicit multi-gate contract ──────────────────────────
  const is_actionable =
    setup_state === 'READY' &&
    !hasFatalDisq &&
    candidate.hasTrigger &&
    candidate.stopLevelClear &&
    (rr === null || rr >= thresholds.min_rr) &&
    quality.setup_score >= thresholds.min_setup_score &&
    readiness.readiness_score >= thresholds.min_readiness_score;

  const missing_items: string[] = [];
  if (!readiness.trigger_confirmed)    missing_items.push('entry trigger not confirmed');
  if (!readiness.confirmation_present) missing_items.push('confirmation signal pending');
  if (!readiness.execution_zone_active) missing_items.push('not at execution zone');
  if (!readiness.invalidation_clear)   missing_items.push('stop level unclear');
  if (!readiness.rr_viable)            missing_items.push('R/R below minimum');

  const actionability: SetupActionability = {
    is_actionable,
    can_become_ready: !hasFatalDisq && quality.setup_score >= 40,
    blocking_disqs:   fatalDisqs.map(d => d.rule),
    missing_items,
  };

  // ── Factors ───────────────────────────────────────────────────────────────
  const supporting: string[] = [];
  const conflicting: string[] = [];

  if (structure_score >= 65)           supporting.push(`Strong structure (${structure_score})`);
  if (context_score   >= 65)           supporting.push(`Favorable context (${context_score})`);
  if (liquidity_score >= 65)           supporting.push(`Good liquidity (${liquidity_score})`);
  if (rr !== null && rr >= 2.0)        supporting.push(`R/R ${rr.toFixed(2)}`);
  if (candidate.hasConfirmation)       supporting.push('Confirmation signal present');
  if (stopQuality >= 70)               supporting.push(`Clean stop structure (${stopQuality})`);

  if (structure_score < 40)            conflicting.push(`Weak structure (${structure_score})`);
  if (context_score   < 40)            conflicting.push(`Context misalignment (${context_score})`);
  if (rr !== null && rr < 1.5)         conflicting.push(`Low R/R ${rr.toFixed(2)}`);
  penalties.forEach(p => conflicting.push(p.reason));

  // ── Assemble ──────────────────────────────────────────────────────────────
  const setup_id  = `s2-${nanoid(8)}`;
  const timestamp = Date.now();

  const result: RichSetup = {
    setup_id,
    symbol:    inp.symbol,
    timeframe: inp.timeframe,
    timestamp,
    setup_classification: candidate.setup_class,
    setup_name:           candidate.setup_name,
    setup_state,
    direction:            candidate.direction,
    setup_score:          quality.setup_score,
    readiness_score:      readiness.readiness_score,
    quality_breakdown:    quality,
    readiness_breakdown:  readiness,
    detected_signals:     candidate.detected_signals,
    supporting_factors:   supporting.slice(0, 5),
    conflicting_factors:  conflicting.slice(0, 3),
    penalties,
    disqualifiers,

    entry_model: {
      trigger:           candidate.entryTriggerText,
      entry_zone_low:    stop !== null ? Math.min(ep, stop) : null,
      entry_zone_high:   ep,
      entry_price_ideal: candidate.entryPriceIdeal,
      confirmation:      candidate.confirmationText,
      timing_note:       candidate.timingNote,
    },

    risk_model: {
      stop_level:         stop,
      stop_basis:         candidate.stopLevelClear ? 'Below/above structural level' : 'Not determinable',
      invalidation_event: candidate.invalidationText,
      stop_quality:       stopQuality,
      stop_note:          stopQuality < 40 ? 'Low stop quality — structural backing weak' : null,
    },

    targets: {
      target_1:     candidate.target1,
      target_2:     candidate.target2,
      risk_reward:  rr,
      target_basis: candidate.targetBasisText,
    },

    actionability,

    audit: {
      setup_id,
      detected_at:        timestamp,
      engine_version:     SETUP_ENGINE_VERSION,
      scoring_version:    SCORING_VERSION,
      schema_version:     SCHEMA_VERSION,
      input_hash:         inputHash,
      scoring_weights:    weights,
      threshold_snapshot: thresholds,
      signal_inputs: {
        has_bos:         !!(smc?.lastBOS),
        has_choch:       !!(smc?.lastCHOCH),
        has_sweep:       !!(smc?.recentSweep?.reclaimed),
        disp_dir:        smc?.displacementDirection ?? 'none',
        disp_str:        smc?.displacementStrength  ?? 0,
        disp_fresh:      smc?.displacementFreshness ?? 0,
        rvol,
        regime_dom:      regime?.dominant    ?? 'unknown',
        regime_posture:  regime?.posture      ?? 'unknown',
        trend_dir:       trend?.trendDirection ?? 'neutral',
        trend_state:     trend?.trendState     ?? 'unknown',
        pd_zone:         smc?.premiumDiscount?.zone ?? 'equilibrium',
        trap_max:        traps[0]?.severity ?? 'None',
        structure_age:   structureAge === Infinity ? null : structureAge,
        trigger_confidence: triggerConfidence,
        stop_quality:    stopQuality,
        computed_rr:     rr,
      },
      detection_trace: [traceEntry],
      state_history:   ['DETECTED', setup_state],
      state_transitions: [
        { state: 'DETECTED' as const, reason: 'Initial candidate detection' },
        { state: setup_state, reason: stateResult.reason },
      ],
    },
  };

  const check = validateRichSetup(result);
  if (!check.ok) {
    console.warn(`[setup2] Schema validation failed for ${candidate.setup_name}:`, check.errors.slice(0, 3));
    return null;
  }

  return result;
}

// ─── Public entry points ──────────────────────────────────────────────────────


// ─── NO_VALID_SETUP builder ───────────────────────────────────────────────────
// Emits a formal contract result when evaluation ran but no setup survived.
// Always non-actionable. Explains why via disqualifiers/detection trace.

function buildNoValidSetup(
  inp:        SetupEngineInput,
  thr:        ThresholdSnapshot,
  weights:    ScoringWeights,
  traces:     DetectionTrace[],
  inputHash:  string,
  suppressed: SuppressedCandidate[],
  no_signal:  boolean,
): RichSetup {
  const setup_id  = `s2-nvs-${nanoid(6)}`;
  const timestamp = Date.now();

  const reason = no_signal
    ? 'No setup signals detected in current market conditions'
    : `${suppressed.length} candidate(s) suppressed — no setup survived classification`;

  const why = suppressed.length > 0
    ? suppressed.map(s => s.reason)
    : ['Insufficient structural signals for any setup family'];

  const nvs: RichSetup = {
    setup_id,
    symbol:    inp.symbol,
    timeframe: inp.timeframe,
    timestamp,
    setup_classification: 'NO_VALID_SETUP',
    setup_name:           'No Valid Setup',
    setup_state:          'DETECTED',
    direction:            'NEUTRAL',
    setup_score:          0,
    readiness_score:      0,
    quality_breakdown: {
      structure_score: 0, context_score: 0, liquidity_score: 0,
      trigger_score: 0, risk_reward_score: 0, execution_quality_score: 0,
      raw_composite: 0, total_penalty: 0, setup_score: 0,
    },
    readiness_breakdown: {
      trigger_confirmed: false, confirmation_present: false,
      execution_zone_active: false, invalidation_clear: false,
      rr_viable: false, timing_quality: 'poor', readiness_score: 0,
    },
    detected_signals:    no_signal ? [] : suppressed.map(s => `Suppressed: ${s.setup_name}`),
    supporting_factors:  [],
    conflicting_factors: why,
    penalties:           [],
    disqualifiers: [{
      rule:   'NO_VALID_SETUP',
      reason,
      fatal:  true,
    }],
    entry_model: {
      trigger:           'No entry trigger — no valid setup',
      entry_zone_low:    null, entry_zone_high: null, entry_price_ideal: null,
      confirmation:      'N/A',
      timing_note:       null,
    },
    risk_model: {
      stop_level: null, stop_basis: 'N/A',
      invalidation_event: 'N/A',
      stop_quality: 0, stop_note: null,
    },
    targets: { target_1: null, target_2: null, risk_reward: null, target_basis: 'N/A' },
    actionability: {
      is_actionable: false,
      can_become_ready: false,
      blocking_disqs: ['NO_VALID_SETUP'],
      missing_items: no_signal
        ? ['No structural signals present']
        : ['No setup candidate survived classification'],
    },
    audit: {
      setup_id,
      detected_at:        timestamp,
      engine_version:     SETUP_ENGINE_VERSION,
      scoring_version:    SCORING_VERSION,
      schema_version:     SCHEMA_VERSION,
      input_hash:         inputHash,
      scoring_weights:    weights,
      threshold_snapshot: thr,
      signal_inputs: {
        has_bos:   !!(inp.smc?.lastBOS),
        has_choch: !!(inp.smc?.lastCHOCH),
        has_sweep: !!(inp.smc?.recentSweep?.reclaimed),
        candidates_detected: suppressed.length,
        no_signal,
      },
      detection_trace: traces,
      state_history:   ['DETECTED'],
      state_transitions: [{ state: 'DETECTED' as const, reason: `NO_VALID_SETUP: ${reason}` }],
    },
  };

  return nvs;
}

export function generateRichSetups(
  inp:            SetupEngineInput,
  thresholds?:    Partial<ThresholdSnapshot>,
): RichSetup[] {
  const weights:   ScoringWeights   = { ...DEFAULT_SCORING_WEIGHTS, ...(inp.weights ?? {}) };
  const thr:       ThresholdSnapshot = { ...DEFAULT_THRESHOLDS, ...(thresholds ?? {}) };

  // Stable input hash for audit/replay
  const { smc, regime, traps, candles } = inp;
  const hashStr = JSON.stringify({
    symbol: inp.symbol, timeframe: inp.timeframe,
    has_bos:   !!(smc?.lastBOS),
    has_choch: !!(smc?.lastCHOCH),
    has_sweep: !!(smc?.recentSweep?.reclaimed),
    disp_dir:  smc?.displacementDirection,
    regime_dom: regime?.dominant,
    trap_count: traps.length,
    candles_len: candles.length,
    last_ts:    candles[candles.length - 1]?.ts,
  });
  const inputHash = hashInputs(hashStr);

  const detection = detectSetupCandidatesFull(inp);
  const { active: candidates, suppressed, no_signal } = detection;

  // Build detection trace — includes active candidates AND suppressed ones
  const traces: DetectionTrace[] = [
    ...candidates.map((c): DetectionTrace => ({
      rule_id:    c.setup_class,
      rule_fired: true,
      conditions: c.detected_signals,
      reason:     c.setup_name,
    })),
    ...suppressed.map((s): DetectionTrace => ({
      rule_id:    s.setup_class,
      rule_fired: false,
      conditions: [],
      reason:     s.reason,
    })),
  ];

  const results: RichSetup[] = [];
  for (let i = 0; i < candidates.length; i++) {
    const assembled = assembleRichSetup(candidates[i], inp, weights, thr, traces[i], inputHash);
    if (assembled) results.push(assembled);
  }

  // NO_VALID_SETUP: emit explicit contract result instead of returning []
  if (results.length === 0) {
    const noSetup = buildNoValidSetup(inp, thr, weights, traces, inputHash, suppressed, no_signal);
    return [noSetup];
  }

  // Sort: READY first, then by setup_score desc
  results.sort((a, b) => {
    const rank = (s: RichSetup) =>
      s.setup_state === 'READY' ? 2 : s.setup_state === 'DEVELOPING' ? 1 : 0;
    return (rank(b) - rank(a)) || (b.setup_score - a.setup_score);
  });

  return results;
}

/** Best single setup or null. */
export function getBestRichSetup(
  inp:         SetupEngineInput,
  thresholds?: Partial<ThresholdSnapshot>,
): RichSetup | null {
  return generateRichSetups(inp, thresholds)[0] ?? null;
}
